import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import {
  createAdminExchangeRate,
  fetchAdminExchangeRates,
  type AdminExchangeRateHistory,
  type AdminExchangeRateHospital,
} from "../api/adminApi";
import { getAuthToken } from "../auth";

function formatRate(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${Number(value).toLocaleString("fr-FR")} FC/USD`;
}

export function HospitalExchangeRatesPage() {
  const { t } = useTranslation();
  const { showToast } = usePageActions();

  const [tableExists, setTableExists] = useState(true);
  const [hospitals, setHospitals] = useState<AdminExchangeRateHospital[]>([]);
  const [history, setHistory] = useState<AdminExchangeRateHistory[]>([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [rate, setRate] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async (hospitalId?: number) => {
    if (!getAuthToken()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchAdminExchangeRates(hospitalId);
      setTableExists(data.table_exists);
      setHospitals(data.hospitals ?? []);
      setHistory(data.history ?? []);
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "Impossible de charger les taux.",
        variant: "warning",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(selectedHospitalId ? Number(selectedHospitalId) : undefined);
  }, [selectedHospitalId]);

  const handleSubmit = async () => {
    const hospitalId = Number(selectedHospitalId);
    const rateValue = Number(rate);
    if (!hospitalId || !rateValue) {
      showToast({ message: "Hôpital et taux obligatoires.", variant: "warning" });
      return;
    }

    setSaving(true);
    try {
      await createAdminExchangeRate({
        hospital_id: hospitalId,
        rate_cdf_per_usd: rateValue,
        effective_from: effectiveFrom || undefined,
        notes: notes.trim() || undefined,
      });
      showToast({ message: "Taux enregistré.", variant: "success" });
      setRate("");
      setNotes("");
      await load(hospitalId);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Enregistrement impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/admin"
        items={[{ label: t.safecheck.nav.exchangeRates }]}
      />

      <div className="mb-4 rounded-lg border border-border bg-card-muted/30 px-4 py-3 text-sm text-muted-foreground">
        Définissez le taux de référence FC/USD par hôpital. Il s&apos;applique aux prochaines
        analyses et conversions facture.
      </div>

      {!tableExists && (
        <div className="mb-4 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
          Table <code>hospital_exchange_rates</code> absente — exécutez{" "}
          <code>sql/upgrade_hospital_exchange_rates.sql</code> sur la base.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="theme-card p-6">
          <h2 className="mb-4 text-base font-semibold">Nouveau taux</h2>
          <div className="space-y-4">
            <FormField label="Hôpital" required>
              <select
                value={selectedHospitalId}
                onChange={(e) => setSelectedHospitalId(e.target.value)}
                className={selectClass}
              >
                <option value="">— Choisir —</option>
                {hospitals.map((h) => (
                  <option key={h.hospital_id} value={String(h.hospital_id)}>
                    {h.hospital_name}
                    {h.rate_cdf_per_usd ? ` (actuel : ${formatRate(h.rate_cdf_per_usd)})` : ""}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Taux FC / 1 USD" required>
              <input
                type="number"
                min={100}
                max={50000}
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="2800"
                className={inputClass}
              />
            </FormField>
            <FormField label="Date d'effet">
              <input
                type="datetime-local"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                className={inputClass}
              />
            </FormField>
            <FormField label="Notes">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={textareaClass}
                rows={2}
              />
            </FormField>
            <button
              type="button"
              disabled={saving || !tableExists}
              onClick={() => void handleSubmit()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
            >
              <Save size={15} />
              {saving ? "Enregistrement…" : "Enregistrer le taux"}
            </button>
          </div>
        </div>

        <div className="theme-card p-6">
          <h2 className="mb-4 text-base font-semibold">Historique</h2>
          {loading ? (
            <p className="text-sm text-muted">Chargement…</p>
          ) : !selectedHospitalId ? (
            <p className="text-sm text-muted">Sélectionnez un hôpital pour voir l&apos;historique.</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted">Aucun taux enregistré pour cet hôpital.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 pr-3">Taux</th>
                    <th className="py-2 pr-3">Effet</th>
                    <th className="py-2 pr-3">Par</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id} className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium">{formatRate(row.rate_cdf_per_usd)}</td>
                      <td className="py-2 pr-3 text-muted">{row.effective_from}</td>
                      <td className="py-2 pr-3 text-muted">{row.created_by_name ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="theme-card mt-6 p-6">
        <h2 className="mb-4 text-base font-semibold">Synthèse par hôpital</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-3 py-2">Hôpital</th>
                <th className="px-3 py-2">Taux actuel</th>
                <th className="px-3 py-2">Dernière mise à jour</th>
              </tr>
            </thead>
            <tbody>
              {hospitals.map((h) => (
                <tr key={h.hospital_id} className="border-b border-border/50">
                  <td className="px-3 py-2">{h.hospital_name}</td>
                  <td className="px-3 py-2 font-medium">{formatRate(h.rate_cdf_per_usd)}</td>
                  <td className="px-3 py-2 text-muted">{h.effective_from ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
